import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MessageCircle, AlertCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1000/api';

const Renewals = ({ token }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('30'); // Default: 30 days

  const fetchExpiries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_URL}/expiries?days=${timeframe}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch expiries');
      setRecords(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, timeframe]);

  useEffect(() => {
    fetchExpiries();
  }, [fetchExpiries]);

  const sendWhatsAppReminder = (record) => {
    if (!record.mobile_number) {
      alert("No mobile number provided for this customer.");
      return;
    }
    const expiryDate = new Date(record.expiry_date).toLocaleDateString('en-GB');
    const vehicleNo = record.vehicle_number.toUpperCase();
    
    const message = `Reminder: Your vehicle ${vehicleNo} PUC is expiring on ${expiryDate}. Renew your PUC today to avoid penalties. – Avantica PUC Center\n\nReminder: તમારા વાહન ${vehicleNo} નું PUC ${expiryDate} ના રોજ Expire થઈ રહ્યું છે. દંડ ટાળવા માટે આજે જ PUC રિન્યુ કરાવો. – Avantica PUC Center\n\nReminder: आपके वाहन ${vehicleNo} का PUC ${expiryDate} को Expire हो रहा है। जुर्माने से बचने के लिए आज ही PUC रिन्यू करवाएं। – Avantica PUC Center`;
    
    const encodedMessage = encodeURIComponent(message);
    // Format number to ensure it starts with country code, assuming India (+91) if 10 digits
    let phone = record.mobile_number.replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;
    
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flex: '1 1 250px' }}>
          <div className="metric-icon" style={{ minWidth: '64px', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))', color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
            <Calendar size={28} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Upcoming Expiries</h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Proactively remind customers to renew</p>
          </div>
        </div>
        
        <div style={{ flex: '1 1 200px' }}>
          <select 
            className="form-control" 
            style={{ width: '100%', cursor: 'pointer' }}
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <option value="3">Expiring in Next 3 Days</option>
            <option value="7">Expiring in Next 7 Days</option>
            <option value="30">Expiring in Next 1 Month</option>
            <option value="90">Expiring in Next 3 Months</option>
            <option value="180">Expiring in Next 6 Months</option>
            <option value="expired">Already Expired (Past Due)</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="glass-card" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertCircle size={20} /> {error}
        </div>
      )}

      <div className="dash-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>{records.length} {records.length === 1 ? 'Vehicle' : 'Vehicles'} Found</h3>
          {loading && <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Loading...</span>}
        </div>
        
        <div className="table-responsive" style={{ border: 'none', background: 'transparent' }}>
          <table className="mobile-cards">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Vehicle Details</th>
                <th>Status</th>
                <th>Expiry Date</th>
                <th style={{ minWidth: '100px', paddingRight: '1rem' }}>Reminder</th>
              </tr>
            </thead>
            <tbody>
              {!loading && records.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    No upcoming expiries found in this timeframe.
                  </td>
                </tr>
              ) : (
                records.map(record => {
                  const isExpired = record.days_left < 0;
                  const isCritical = record.days_left >= 0 && record.days_left <= 3;
                  
                  return (
                    <tr key={record.id}>
                      <td data-label="Customer">
                        <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{record.customer_name || '-'}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{record.mobile_number || 'No Number'}</div>
                      </td>
                      <td data-label="Vehicle Details">
                        <div style={{ fontWeight: 700, letterSpacing: '0.5px' }}>{record.vehicle_number ? record.vehicle_number.toUpperCase() : '-'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {record.vehicle_type && <span>{record.vehicle_type}</span>}
                          {record.fuel_type && <span>• {record.fuel_type}</span>}
                        </div>
                      </td>
                      <td data-label="Status">
                        {isExpired ? (
                          <span className="badge badge-petrol" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <AlertCircle size={14} /> Expired {Math.abs(record.days_left)} days ago
                          </span>
                        ) : (
                          <span className={`badge ${isCritical ? 'badge-petrol' : 'badge-diesel'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={14} /> In {record.days_left} days
                          </span>
                        )}
                      </td>
                      <td data-label="Expiry Date" style={{ fontWeight: 600 }}>
                        {new Date(record.expiry_date).toLocaleDateString('en-GB')}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: '2px' }}>
                          (Issued: {new Date(record.record_date).toLocaleDateString('en-GB')})
                        </div>
                      </td>
                      <td data-label="Reminder" style={{ paddingRight: '1rem' }}>
                        <button 
                          className="whatsapp-btn" 
                          onClick={() => sendWhatsAppReminder(record)}
                          disabled={!record.mobile_number}
                        >
                          <MessageCircle size={16} /> WhatsApp
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Renewals;
